import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useSnackbar } from 'components/common/SnackbarNotification';
import {
  UserBuAccessFormModal,
  UserBuAccessViewModal,
} from 'components/modals/modules/admin-module/UserBuAccessModal';
import type {
  UserBuAccessFormData,
  UserBuAccessRecord,
} from 'components/modals/modules/admin-module/UserBuAccessModal';
import { formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { useUserAccess, useUsersLookup, useAssignAccess, useUpdateAccess, useRevokeAccess, useEntities, useBusinessUnits } from 'hooks/api';
import { GridLoadingSkeleton } from 'components/common';
// ============================================================================
// INITIAL FORM DATA
// ============================================================================

const emptyFormData: UserBuAccessFormData = {
  userId: '',
  userName: '',
  entityId: '',
  businessUnitId: '',
  businessUnitIds: [],
  role: '',
  isPrimary: false,
};

// ============================================================================
// MAIN USER BRANCH ACCESS SCREEN
// ============================================================================

const UserBuAccessScreen: React.FC = () => {
  // API hooks
  const { data: apiData, loading: apiLoading, error: apiError, refetch } = useUserAccess();
  const { data: usersLookupData, loading: usersLoading, refetch: refetchUsers } = useUsersLookup();
  const { assignAccess } = useAssignAccess();
  const { updateAccess } = useUpdateAccess();
  const { revokeAccess } = useRevokeAccess();
  const { data: entitiesData } = useEntities();
  const { data: businessUnitsData } = useBusinessUnits();

  // Data from API
  const accessRecords = (apiData as unknown as UserBuAccessRecord[]) || [];

  // Dropdown options
  const userOptions = useMemo(() => {
    return (usersLookupData || []).map((u: any) => ({ value: u.userId, label: u.displayName || u.email, role: u.role || '' }));
  }, [usersLookupData]);

  const entityOptions = useMemo(() => {
    
    return entitiesData.map((e: any) => ({ value: e.id, label: e.entityName }));
  }, [entitiesData]);

  const branchOptions = useMemo(() => {
    
    return businessUnitsData.map((bu: any) => ({
      value: bu.id,
      label: `${bu.buName} (${bu.buCode})`,
      entityId: bu.entityId,
    }));
  }, [businessUnitsData]);

  // UI state
  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [, setSelectedRows] = useState<UserBuAccessRecord[]>([]);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'assign' | 'edit'>('assign');
  const [selectedRecord, setSelectedRecord] = useState<UserBuAccessRecord | null>(null);
  const [formData, setFormData] = useState<UserBuAccessFormData>(emptyFormData);

  // Filter state
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    variant: 'default' | 'danger';
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', variant: 'default', onConfirm: () => {} });

  const { showMessage } = useSnackbar();

  // ============================================================================
  // FILTERED DATA
  // ============================================================================

  const filteredData = useMemo(() => {
    return accessRecords.filter((record) => {
      const matchesSearch = !searchQuery ||
        record.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record.buName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record.buCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record.entityName.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === '' ||
        (statusFilter === 'Active' && record.isActive) ||
        (statusFilter === 'Revoked' && !record.isActive);

      const matchesRole = roleFilter === '' || record.accessRole === roleFilter;

      const matchesEntity = entityFilter === '' || record.entityId === entityFilter;

      return matchesSearch && matchesStatus && matchesRole && matchesEntity;
    });
  }, [accessRecords, searchQuery, statusFilter, roleFilter, entityFilter]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleAssignNew = useCallback(() => {
    refetchUsers();
    setFormMode('assign');
    setSelectedRecord(null);
    setFormData(emptyFormData);
    setFormModalOpen(true);
  }, [refetchUsers]);

  const handleEdit = useCallback((record: UserBuAccessRecord) => {
    setFormMode('edit');
    setSelectedRecord(record);
    setFormData({
      userId: record.userId,
      userName: record.userName,
      entityId: record.entityId,
      businessUnitId: record.businessUnitId,
      businessUnitIds: [record.businessUnitId],
      role: record.accessRole,
      isPrimary: record.isPrimary,
    });
    setFormModalOpen(true);
  }, []);

  const handleView = useCallback((record: UserBuAccessRecord) => {
    setSelectedRecord(record);
    setViewModalOpen(true);
  }, []);

  const handleSaveForm = useCallback(() => {
    const action = formMode === 'assign' ? 'Assign' : 'Save';
    const branchCount = formMode === 'assign' ? formData.businessUnitIds.length : 1;
    const message = formMode === 'assign'
      ? `Are you sure you want to assign access to ${branchCount} branch${branchCount > 1 ? 'es' : ''} for this user?`
      : 'Are you sure you want to save changes to this access record?';

    setConfirmDialog({
      open: true,
      title: `${action} Access`,
      message,
      variant: 'default',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          if (formMode === 'assign') {
              const primaryBuId = formData.businessUnitIds.length > 1
                ? formData.businessUnitId
                : (formData.isPrimary ? formData.businessUnitIds[0] : '');

              for (const buId of formData.businessUnitIds) {
                await assignAccess({
                  userId: formData.userId,
                  userName: formData.userName,
                  entityId: formData.entityId,
                  businessUnitId: buId,
                  isPrimary: buId === primaryBuId,
                });
              }
              await refetch();
            } else if (selectedRecord) {
              await updateAccess(selectedRecord.id, {
                isPrimary: formData.isPrimary,
              });
              await refetch();
            }

          const count = formMode === 'assign' ? formData.businessUnitIds.length : 1;
          showMessage(
            formMode === 'assign'
              ? `Access assigned to ${count} branch${count > 1 ? 'es' : ''} successfully`
              : 'Access updated successfully',
            'success'
          );
          setFormModalOpen(false);
        } catch (err: any) {
          const apiMessage = err?.response?.data?.message;
          showMessage(apiMessage || `Failed to ${formMode === 'assign' ? 'assign' : 'update'} access`, 'error');
        }
      },
    });
  }, [formMode, formData, accessRecords.length, selectedRecord, entityOptions, branchOptions, showMessage, assignAccess, updateAccess, refetch]);

  const handleRevoke = useCallback((record: UserBuAccessRecord) => {
    setConfirmDialog({
      open: true,
      title: 'Revoke Access',
      message: `Are you sure you want to revoke "${record.userName}"'s access to "${record.buName}" (${record.buCode})?`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          await revokeAccess(record.id);
            await refetch();

          showMessage('Access revoked successfully', 'success');
        } catch (err: any) {
          const apiMessage = err?.response?.data?.message;
          showMessage(apiMessage || 'Failed to revoke access', 'error');
        }
      },
    });
  }, [showMessage, revokeAccess, refetch]);

  // ============================================================================
  // ROW ACTIONS
  // ============================================================================

  const getRowActions = useCallback((row: UserBuAccessRecord): ActionMenuItem[] => {
    const actions: ActionMenuItem[] = [
      {
        id: 'view',
        label: 'View Details',
        icon: <IconSystem.eye size={18} />,
        onClick: () => handleView(row),
      },
    ];

    if (row.isActive) {
      actions.push(
        {
          id: 'edit',
          label: 'Edit',
          icon: <IconSystem.edit size={18} />,
          onClick: () => handleEdit(row),
        },
        {
          id: 'revoke',
          label: 'Revoke',
          icon: <IconSystem.xCircle size={18} />,
          onClick: () => handleRevoke(row),
          variant: 'danger' as const,
        },
      );
    }

    return actions;
  }, [handleView, handleEdit, handleRevoke]);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const tableColumns: SyutGridColumn<UserBuAccessRecord>[] = useMemo(() => [
    { id: 'userName', header: 'User', accessorKey: 'userName', size: 170 },
    { id: 'entityName', header: 'Entity', accessorKey: 'entityName', size: 170 },
    { id: 'buName', header: 'Branch', accessorKey: 'buName', size: 160 },
    { id: 'buCode', header: 'BU Code', accessorKey: 'buCode', size: 100 },
    {
      id: 'accessRole',
      header: 'Role',
      accessorKey: 'accessRole',
      size: 130,
      cell: ({ getValue }: any) => {
        const role = getValue() as string;
        const labels: Record<string, string> = {
          BRANCH_USER: 'Branch User',
          APPROVER: 'Approver',
          ADMIN: 'Admin',
          SYSTEM: 'System',
        };
        return labels[role] || role;
      },
    },
    {
      id: 'isPrimary',
      header: 'Primary',
      accessorKey: 'isPrimary',
      size: 90,
      cell: ({ getValue }: any) => {
        const isPrimary = getValue();
        return (
          <span style={getStatusBadgeStyle(isPrimary ? 'ACTIVE' : 'INACTIVE')}>
            {isPrimary ? 'Yes' : 'No'}
          </span>
        );
      },
    },
    {
      id: 'isActive',
      header: 'Status',
      accessorKey: 'isActive',
      size: 100,
      cell: ({ getValue }: any) => {
        const isActive = getValue();
        return (
          <span style={getStatusBadgeStyle(isActive ? 'ACTIVE' : 'INACTIVE')}>
            {isActive ? 'Active' : 'Revoked'}
          </span>
        );
      },
    },
    {
      id: 'grantedAt',
      header: 'Granted At',
      accessorKey: 'grantedAt',
      size: 120,
      cell: ({ getValue }: any) => formatDate(getValue()),
    },
  ], []);

  // ============================================================================
  // VIEW MODE & ACTION BUTTONS
  // ============================================================================

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View', disabled: true },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View (Not Applicable)', disabled: true },
  ];

  const actionButtons: ActionButton[] = [
    {
      id: 'assign',
      icon: <IconSystem.add size={18} />,
      label: 'Assign Access',
      onClick: handleAssignNew,
      tooltip: 'Assign Branch Access',
      variant: 'icon',
      visible: true,
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => refetch(),
      tooltip: 'Refresh',
      variant: 'icon',
    },
  ];

  // Entity filter options (for filter panel)
  const entityFilterOptions = useMemo(() => {
    const allOption = { value: '', label: 'All Entities' };
    
    return [allOption, ...entityOptions];
  }, [accessRecords, entityOptions]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="User Branch Access"
        icon={<IconSystem.users size={20} />}
        description="Manage user access to branches"
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search users or branches...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        actions={actionButtons}
      />

      <div className="module-content">
        {/* Error */}
        {apiError && accessRecords.length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load access records</div>
              <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
            </div>
          </div>
        )}

        {apiLoading && accessRecords.length === 0 ? (
          <GridLoadingSkeleton columns={6} />
        ) : (
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={filteredData}
                columns={tableColumns}
                rowActions={getRowActions}
                enableRowSelection
                enablePagination
                enableColumnFilters={false}
                onSelectionChange={setSelectedRows}
                emptyMessage="No access records found"
              />
            )}
          </div>

          {/* Filter Panel */}
          <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>

            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Entity"
                  options={entityFilterOptions}
                  value={entityFilter}
                  onChange={(val: string | string[] | null) => setEntityFilter((val as string) || '')}
                  placeholder="Search entities..."
                  isSearchable={true}
                  isClearable={false}
                  size="sm"
                />
              </div>

              <div className="filter-section">
                <SyutSelect
                  label="Access Role"
                  options={[
                    { value: '', label: 'All Roles' },
                    { value: 'BRANCH_USER', label: 'Branch User' },
                    { value: 'APPROVER', label: 'Approver' },
                    { value: 'ADMIN', label: 'Admin' },
                  ]}
                  value={roleFilter}
                  onChange={(val: string | string[] | null) => setRoleFilter((val as string) || '')}
                  placeholder="Search roles..."
                  isSearchable={true}
                  isClearable={false}
                  size="sm"
                />
              </div>

              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={[
                    { value: '', label: 'All Status' },
                    { value: 'Active', label: 'Active' },
                    { value: 'Revoked', label: 'Revoked' },
                  ]}
                  value={statusFilter}
                  onChange={(val: string | string[] | null) => setStatusFilter((val as string) || '')}
                  placeholder="Search status..."
                  isSearchable={true}
                  isClearable={false}
                  size="sm"
                />
              </div>
            </div>

            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setStatusFilter(''); setRoleFilter(''); setEntityFilter(''); }}>
                Clear
              </button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>
                Apply
              </button>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Form Modal */}
      <UserBuAccessFormModal
        isOpen={formModalOpen}
        mode={formMode}
        formData={formData}
        userOptions={userOptions}
        entityOptions={entityOptions}
        branchOptions={branchOptions}
        onFormDataChange={setFormData}
        onSave={handleSaveForm}
        onClose={() => setFormModalOpen(false)}
        onRefreshUsers={refetchUsers}
        usersLoading={usersLoading}
      />

      {/* View Modal */}
      <UserBuAccessViewModal
        isOpen={viewModalOpen}
        record={selectedRecord}
        onClose={() => setViewModalOpen(false)}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        confirmLabel={confirmDialog.variant === 'danger' ? 'Revoke' : 'Confirm'}
      />
    </div>
  );
};

export default UserBuAccessScreen;
